import { Events } from "../../js/utils/events.js";
import { setConnection, getSessionId, setAccountThreshold } from "../../js/utils/roaming_settings.js";
import { isNaaSupported, exchangeToken, linkIdentity } from "../../js/utils/auth.js";

let initialized = false;

// The silent restore path (context fetch + NAA token exchange) can hang without
// resolving — the MSAL host bridge has no timeout — leaving the pane stuck on
// "Connecting…". The watchdog falls back to the manual Connect prompt.
const RESTORE_TIMEOUT_MS = 10000;

export async function initConnect() {
  if (initialized) return;
  initialized = true;
  Events.on("click", "oa-connect", onConnect);

  const watchdog = setTimeout(showPrompt, RESTORE_TIMEOUT_MS);
  // Validate the stored session by fetching its context. Present -> the session
  // is good, reuse it and load files. Absent (no session, or expired/incomplete)
  // -> silently reconnect on NAA hosts, otherwise prompt to connect.
  if (await fetchContext(getSessionId())) {
    clearTimeout(watchdog);
    window.htmx.trigger("#load-files", "connected");
    return;
  }
  if (isNaaSupported()) {
    await reconnect();   // always ends on the prompt or the files view
    clearTimeout(watchdog);
    return;
  }
  clearTimeout(watchdog);
  showPrompt();
}

/** Reveal the manual Connect prompt, hiding the "Connecting…" label. */
function showPrompt() {
  document.getElementById("connect-connecting")?.classList.add("hidden");
  document.getElementById("connect-prompt")?.classList.remove("hidden");
}

/** Show the "Connecting…" label, hiding the manual Connect prompt. */
function showConnecting() {
  document.getElementById("connect-prompt")?.classList.add("hidden");
  document.getElementById("connect-connecting")?.classList.remove("hidden");
}

/**
 * Mint a fresh session via the NAA token exchange. Called from initConnect only
 * when the stored session was missing/expired (the validity check lives there),
 * so this just acquires a token, exchanges it, and loads files.
 */
async function reconnect() {
  try {
    const { status, body } = await exchangeToken();
    if (status === 200 && body.linked && body.session) {
      await setConnection({ session: body.session, username: body.username });
      await fetchContext(body.session); // mirrors the account's attachment threshold
      window.htmx.trigger("#load-files", "connected");
      return;
    }
    // Not linked -> let the user connect manually.
    showPrompt();
  } catch (err) {
    // NAA unavailable / token acquisition failed -> the user can still click
    // Connect (dialog-login fallback). Surface the reason; do not rethrow.
    console.error("[NAA] reconnect failed:", err);
    showPrompt();
  }
}

/**
 * GET the pane's account context for a session; null if the session is invalid.
 * Also mirrors the account's attachment threshold into roaming settings, so the
 * Files view can seed its setting from it without a second round trip.
 */
async function fetchContext(session) {
  if (!session) return null;
  let context = null;
  try {
    const resp = await fetch("/outlook/auth/context", {
      headers: { Authorization: `Bearer ${session}` },
    });
    if (resp.ok) context = await resp.json();
  } catch {
    context = null;
  }
  // Fail closed. Both connect paths load the Files view whether or not this
  // answered, so keeping the last known threshold would let an unreachable (or
  // newly signed-in) account be measured against the previous one's limit.
  await setAccountThreshold(context && context.attachment_threshold);
  return context;
}

function openDialogLogin() {
  return new Promise((resolve) => {
    const loginUrl = window.location.origin + "/app/login/outlook";
    Office.context.ui.displayDialogAsync(loginUrl, { height: 60, width: 40, promptBeforeOpen: false }, (res) => {
      if (res.status !== Office.AsyncResultStatus.Succeeded) return resolve(null);
      const dialog = res.value;
      let settled = false;
      const done = (value) => {
        if (settled) return;
        settled = true;
        try { dialog.close(); } catch { /* already closed by the user/host */ }
        resolve(value);
      };
      dialog.addEventHandler(Office.EventType.DialogMessageReceived, (arg) => {
        let msg; try { msg = JSON.parse(arg.message); } catch { return; }
        if (msg.type !== "connected") return;
        done({ session: msg.session, username: msg.username });
      });
      // The dialog can end without ever sending a message: the user closes it
      // (12006), the page fails to load (12002), or a redirect crosses
      // origins/zones (12003). Without this the promise would never resolve and
      // the pane would hang on "Connecting…". Resolve null -> back to the prompt
      // so the user can retry.
      dialog.addEventHandler(Office.EventType.DialogEventReceived, () => done(null));
    });
  });
}

async function onConnect() {
  showConnecting();   // dialog opening — reflect progress in the pane
  const result = await openDialogLogin();
  if (!result) { showPrompt(); return; }   // cancelled -> back to the prompt
  // Persist FIRST — saveAsync is async; transitioning before it completes
  // would lose the connection on reload.
  await setConnection({ session: result.session, username: result.username });
  // If NAA is available, bind this identity so future opens are zero-click.
  // Best-effort: a link failure must NOT block connecting (session already
  // persisted above), but it must be visible — don't swallow it.
  if (isNaaSupported()) {
    try {
      await linkIdentity(result.session);
    } catch (err) {
      console.error("[NAA] link failed (still connected for this session):", err);
    }
  }
  await fetchContext(result.session); // mirrors the account's attachment threshold
  window.htmx.trigger("#load-files", "connected");
}
